import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type Graph from 'graphology';
import { useEffect, useMemo } from 'react';
import type { Settings } from 'sigma/settings';

import { getFullGraph, type GraphDto } from '../api/graph.js';
import { useApi } from '../api/use-api.js';
import { buildGraphologyGraph } from '../lib/graph-adapter.js';
import type { SigmaEdgeAttributes, SigmaNodeAttributes } from '../lib/graph-node-attributes.js';
import {
  hasActiveHighlights,
  makeHighlightNodeReducer,
  makeSelectionNodeReducer,
  type HighlightSets,
} from '../lib/highlight-node-reducer.js';
import { useUiStore } from '../state/ui-store.js';

import { useHighlightAnimation } from './use-highlight-animation.js';
import { useSigma, type UseSigmaReturn } from './use-sigma.js';

export interface UseRepoGraphOptions {
  readonly slug: string;
  readonly onNodeSelect?: (nodeId: string | null) => void;
  readonly communityMemberships?: ReadonlyMap<string, number>;
}

export interface UseRepoGraphReturn {
  readonly query: UseQueryResult<GraphDto>;
  readonly sigma: UseSigmaReturn;
  readonly graphology: Graph<SigmaNodeAttributes, SigmaEdgeAttributes> | null;
}

function bfsNeighbors(
  graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>,
  startId: string,
  depth: number,
): Set<string> {
  const visited = new Set<string>();
  let frontier = new Set<string>([startId]);
  for (let d = 0; d < depth; d++) {
    const next = new Set<string>();
    for (const nodeId of frontier) {
      for (const neighbor of graph.neighbors(nodeId)) {
        if (!visited.has(neighbor) && neighbor !== startId) {
          visited.add(neighbor);
          next.add(neighbor);
        }
      }
    }
    frontier = next;
    if (frontier.size === 0) break;
  }
  return visited;
}

export function useRepoGraph({
  slug,
  onNodeSelect,
  communityMemberships,
}: UseRepoGraphOptions): UseRepoGraphReturn {
  const api = useApi();
  const query = useQuery({
    queryKey: ['repo-graph', slug],
    queryFn: () => getFullGraph(api, slug),
    staleTime: 60_000,
  });

  const highlights = useUiStore((s) => s.highlights);
  const selectedNodeId = useUiStore((s) => s.selectedNodeId);
  const focusDepth = useUiStore((s) => s.focusDepth);

  const highlightSets = useMemo<HighlightSets>(
    () => ({
      citation: new Set(highlights.citation),
      tool: new Set(highlights.tool),
      blast: new Set(highlights.blast),
      visible: highlights.visible,
    }),
    [highlights.citation, highlights.tool, highlights.blast, highlights.visible],
  );

  const active = hasActiveHighlights(highlightSets);

  const sigmaApi = useSigma({
    onNodeClick: (id) => onNodeSelect?.(id),
    onStageClick: () => onNodeSelect?.(null),
  });

  const pulse = useHighlightAnimation(sigmaApi.sigma, active);

  const graphology = useMemo(() => {
    if (!query.data) return null;
    return buildGraphologyGraph(query.data, {
      ...(communityMemberships ? { communityMemberships } : {}),
    });
  }, [query.data, communityMemberships]);

  // Depend only on the stable setGraph callback and the Sigma instance (not the
  // whole sigmaApi object, which is a new reference every render and would
  // cause FA2 to be killed and restarted in an infinite cycle).
  const { setGraph: setSigmaGraph, sigma: sigmaInstance } = sigmaApi;
  useEffect(() => {
    if (!graphology || !sigmaInstance) return;
    setSigmaGraph(graphology);
  }, [graphology, sigmaInstance, setSigmaGraph]);

  // BFS neighbors for the selected node
  const selectionNeighbors = useMemo(() => {
    if (!selectedNodeId || !graphology?.hasNode(selectedNodeId)) {
      return new Set<string>();
    }
    return bfsNeighbors(graphology, selectedNodeId, focusDepth);
  }, [selectedNodeId, focusDepth, graphology]);

  // Active edge IDs: edges touching selected node or its neighbors
  const activeEdgeIds = useMemo(() => {
    if (!selectedNodeId || !graphology) return null;
    const ids = new Set<string>();
    const relevant = [selectedNodeId, ...selectionNeighbors];
    for (const nodeId of relevant) {
      if (graphology.hasNode(nodeId)) {
        for (const edgeId of graphology.edges(nodeId)) {
          ids.add(edgeId);
        }
      }
    }
    return ids;
  }, [selectedNodeId, selectionNeighbors, graphology]);

  // Composed node reducer: selection > AI highlights > passthrough
  const nodeReducer = useMemo(() => {
    if (selectedNodeId && graphology?.hasNode(selectedNodeId)) {
      return makeSelectionNodeReducer(selectedNodeId, selectionNeighbors);
    }
    if (active) return makeHighlightNodeReducer(highlightSets, pulse);
    return null;
  }, [selectedNodeId, selectionNeighbors, graphology, active, highlightSets, pulse]);

  useEffect(() => {
    const sigma = sigmaApi.sigma;
    if (!sigma) return;
    sigma.setSetting('nodeReducer', nodeReducer as Settings['nodeReducer']);
    if (!selectedNodeId && !active) sigma.refresh();
  }, [sigmaApi.sigma, nodeReducer, selectedNodeId, active]);

  // Edge reducer: highlight edges near selection, dim the rest
  useEffect(() => {
    const sigma = sigmaApi.sigma;
    if (!sigma) return;
    if (activeEdgeIds) {
      sigma.setSetting('edgeReducer', (edge: string, data: Record<string, unknown>) => {
        const sz = (data.size as number) ?? 1;
        if (activeEdgeIds.has(edge)) {
          return { ...data, size: Math.max(sz * 2.5, 1.5), zIndex: 1 };
        }
        return { ...data, color: '#0a0a12', size: sz * 0.2, zIndex: 0 };
      });
    } else {
      sigma.setSetting('edgeReducer', null);
    }
    sigma.refresh();
  }, [sigmaApi.sigma, activeEdgeIds]);

  return { query, sigma: sigmaApi, graphology };
}
